(function () {
  window.ConcepStoreToastInstances = window.ConcepStoreToastInstances || {};

  const scriptEl = document.currentScript;
  const configEl = scriptEl ? scriptEl.previousElementSibling : null;
  const configId = configEl && configEl.id;
  const region = configId
    ? document.querySelector(`[data-site-toast-region][data-config-id="${configId}"]`)
    : null;

  if (!configEl || !region) return;

  let config = {};

  try {
    config = JSON.parse(configEl.textContent);
  } catch (error) {
    console.warn('Site toast config is invalid JSON.', error);
    return;
  }

  const defaults = config.defaults || {};
  const isDesignMode = Boolean(config.designMode || (window.Shopify && window.Shopify.designMode));
  const activeToasts = new Map();
  const cleanupHandlers = [];
  const timers = new Set();
  const stackControls = createElement('div', 'site-toast-stack-controls');
  const stackToggle = createElement('button', 'site-toast-stack-toggle', 'View all');
  const dismissAll = createElement('button', 'site-toast-dismiss-all', 'Dismiss all');
  stackToggle.type = 'button';
  dismissAll.type = 'button';
  stackToggle.hidden = true;
  dismissAll.hidden = true;
  stackToggle.setAttribute('aria-expanded', 'false');
  dismissAll.setAttribute('aria-label', 'Dismiss all notifications');
  stackControls.hidden = true;
  stackControls.append(stackToggle, dismissAll);
  region.append(stackControls);

  if (window.ConcepStoreToastInstances[config.sectionId]) {
    window.ConcepStoreToastInstances[config.sectionId].destroy();
  }

  function addCleanup(target, eventName, handler, options) {
    target.addEventListener(eventName, handler, options);
    cleanupHandlers.push(() => target.removeEventListener(eventName, handler, options));
  }

  function safeStorageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function safeStorageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      // Ignore storage failures so privacy settings do not break notifications.
    }
  }

  function setManagedTimeout(callback, delay) {
    const timeout = setTimeout(() => {
      timers.delete(timeout);
      callback();
    }, delay);

    timers.add(timeout);
    return timeout;
  }

  function normalizeToast(toast) {
    const normalizedToast = Object.assign(
      {
        enabled: true,
        delayBeforeShow: 0,
        displayDuration: 8000,
        recurrenceDays: 0,
        showOnMobile: true,
        includePages: [],
        excludePages: []
      },
      defaults,
      toast
    );

    normalizedToast.includePages = normalizedToast.includePages.filter(Boolean);
    normalizedToast.excludePages = normalizedToast.excludePages.filter(Boolean);

    return normalizedToast;
  }

  function pageMatches(toast) {
    const path = window.location.pathname;
    const mobileMedia = window.matchMedia('(max-width: 768.98px)');

    if (!toast.showOnMobile && mobileMedia.matches) return false;

    const excluded = toast.excludePages.some((excludePath) => path.indexOf(excludePath) !== -1);

    if (excluded) return false;

    if (toast.includePages.length > 0) {
      return toast.includePages.some((includePath) => path.indexOf(includePath) !== -1);
    }

    return true;
  }

  function recurrenceAllows(toast) {
    const storageKey = toast.storageKey || `cs_toast_${toast.id}`;
    const lastDismissed = safeStorageGet(storageKey);

    if (!lastDismissed) return true;
    if (toast.recurrenceDays === 0) return false;

    const daysSinceDismissed = (Date.now() - parseInt(lastDismissed, 10)) / (1000 * 60 * 60 * 24);
    return daysSinceDismissed >= toast.recurrenceDays;
  }

  function createElement(tagName, className, text) {
    const element = document.createElement(tagName);
    element.className = className;

    if (text) {
      element.textContent = text;
    }

    return element;
  }

  function setStyleProperty(element, property, value) {
    if (!value) return;
    element.style.setProperty(property, value);
  }

  function applyToastStyles(item, toast) {
    const styles = toast.styles || {};

    setStyleProperty(item, '--site-toast-bg', styles.backgroundColor);
    setStyleProperty(item, '--site-toast-bg-gradient', styles.backgroundGradient);
    setStyleProperty(item, '--site-toast-title-color', styles.titleColor);
    setStyleProperty(item, '--site-toast-body-color', styles.bodyColor);
    setStyleProperty(item, '--site-toast-button-bg', styles.buttonBackgroundColor);
    setStyleProperty(item, '--site-toast-button-color', styles.buttonTextColor);
  }

  function buildToast(toast) {
    const item = createElement('div', 'site-toast');
    item.dataset.toastId = toast.id;
    item.setAttribute('role', 'group');
    item.setAttribute('aria-label', toast.title);
    item.tabIndex = 0;
    item.classList.toggle('has-action', Boolean(toast.href));
    applyToastStyles(item, toast);

    const main = createElement('div', 'site-toast__main');
    const title = createElement('p', 'site-toast__title', toast.title);
    const close = createElement('button', 'site-toast__close');

    close.type = 'button';
    close.setAttribute('aria-label', 'Close notification');
    close.innerHTML = '<svg viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"></path></svg>';

    main.append(title);

    if (toast.body) {
      main.append(createElement('p', 'site-toast__body', toast.body));
    }

    if (toast.href) {
      const action = createElement('a', 'site-toast__action', toast.actionLabel || 'Open');
      action.href = toast.href;
      action.setAttribute('aria-label', toast.actionLabel || `Open ${toast.title}`);

      action.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        handleToastLink(toast, action);
      });

      main.append(action);
    }

    item.append(main);

    if (toast.imageUrl) {
      const media = createElement('img', 'site-toast__media');
      media.src = toast.imageUrl;
      media.alt = toast.imageAlt || '';
      media.loading = 'lazy';
      item.append(media);
    }

    item.append(close);

    close.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      dismissToast(toast.id, true);
    });

    item.addEventListener('keydown', (event) => {
      if (event.target.closest('.site-toast__close, .site-toast__action')) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;

      if (!region.classList.contains('is-expanded') && activeToasts.size > 1) {
        event.preventDefault();
        expandStack();
      }
    });

    return { item };
  }

  function markDismissed(toast) {
    const storageKey = toast.storageKey || `cs_toast_${toast.id}`;
    safeStorageSet(storageKey, Date.now().toString());
  }

  function handleToastLink(toast, opener) {
    if (isDesignMode) return;

    markDismissed(toast);

    if (toast.href.indexOf('#personal-shopper') === 0) {
      const drawer = document.querySelector('#personal-shopper-drawer');

      if (drawer && typeof drawer.open === 'function') {
        if (window.history && window.history.pushState) {
          window.history.pushState(null, '', toast.href);
        }

        drawer.open(opener);
        return;
      }
    }

    window.location.href = toast.href;
  }

  function dismissToast(toastId, rememberDismissal, immediate) {
    const activeToast = activeToasts.get(toastId);

    if (!activeToast) return;

    if (rememberDismissal) {
      markDismissed(activeToast.toast);
    }

    cancelAnimationFrame(activeToast.animationFrame);

    if (immediate) {
      activeToast.item.remove();
      activeToasts.delete(toastId);
      updateStackState();
      return;
    }

    activeToast.item.classList.add('is-hiding');

    setManagedTimeout(() => {
      activeToast.item.remove();
      activeToasts.delete(toastId);
      updateStackState();
    }, 240);
  }

  function startTimer(toast) {
    if (!toast.displayDuration || toast.displayDuration <= 0) return null;

    let startTime = null;

    function animate(timestamp) {
      if (!startTime) startTime = timestamp;

      const elapsed = timestamp - startTime;

      if (elapsed < toast.displayDuration) {
        const activeToast = activeToasts.get(toast.id);

        if (activeToast) {
          activeToast.animationFrame = requestAnimationFrame(animate);
        }
      } else {
        dismissToast(toast.id, false);
      }
    }

    return requestAnimationFrame(animate);
  }

  function showToast(rawToast, options) {
    const toast = normalizeToast(Object.assign({}, rawToast, options));

    if (!toast.enabled || !toast.id || !toast.title) return;
    if (activeToasts.has(toast.id)) {
      if (!toast.force) return;
      dismissToast(toast.id, false, true);
    }
    if (!toast.force && (!pageMatches(toast) || !recurrenceAllows(toast))) return;

    const { item } = buildToast(toast);
    region.insertBefore(item, stackControls);

    activeToasts.set(toast.id, {
      toast,
      item,
      animationFrame: null
    });
    updateStackState();

    requestAnimationFrame(() => {
      item.classList.add('is-visible');
      const activeToast = activeToasts.get(toast.id);

      if (activeToast) {
        activeToast.animationFrame = startTimer(toast);
      }
    });
  }

  function queueToast(rawToast) {
    const toast = normalizeToast(rawToast);
    setManagedTimeout(() => showToast(toast), toast.delayBeforeShow);
  }

  function updateStackState() {
    region.classList.toggle('has-multiple', activeToasts.size > 1);

    if (activeToasts.size <= 1) {
      region.classList.remove('is-expanded');
    }

    stackControls.hidden = activeToasts.size <= 1;
    stackToggle.hidden = activeToasts.size <= 1;
    dismissAll.hidden = !defaults.showDismissAll || activeToasts.size <= 1;
    stackToggle.textContent = region.classList.contains('is-expanded') ? 'Collapse' : `View all (${activeToasts.size})`;
    stackToggle.setAttribute('aria-expanded', region.classList.contains('is-expanded') ? 'true' : 'false');
    stackToggle.setAttribute(
      'aria-label',
      region.classList.contains('is-expanded') ? 'Collapse notifications' : `View all ${activeToasts.size} notifications`
    );

    Array.from(region.querySelectorAll('.site-toast')).forEach((toast, index) => {
      const isCollapsedBehindTop = !region.classList.contains('is-expanded') && activeToasts.size > 1 && index > 0;

      toast.style.setProperty('--toast-stack-index', index);
      toast.style.setProperty('--toast-stack-offset', `${index * 8}px`);
      toast.style.setProperty('--toast-stack-offset-mobile', `${index * 7}px`);
      toast.style.setProperty('--toast-stack-scale', Math.max(0.92, 1 - index * 0.025));
      toast.style.zIndex = 20 - index;
      toast.tabIndex = isCollapsedBehindTop ? -1 : 0;
      toast.setAttribute('aria-hidden', isCollapsedBehindTop ? 'true' : 'false');
    });
  }

  function expandStack() {
    if (activeToasts.size > 1) {
      region.classList.add('is-expanded');
      updateStackState();
    }
  }

  function collapseStack() {
    region.classList.remove('is-expanded');
    updateStackState();
  }

  function dismissAllToasts() {
    Array.from(activeToasts.keys()).forEach((toastId) => {
      dismissToast(toastId, true);
    });
  }

  function resetPreviewToasts() {
    activeToasts.forEach((activeToast) => {
      cancelAnimationFrame(activeToast.animationFrame);
    });

    activeToasts.clear();
    region.querySelectorAll('.site-toast').forEach((toast) => toast.remove());
    updateStackState();
  }

  function previewToast(blockId) {
    const toasts = (config.toasts || []).filter((toast) => toast.enabled);
    const toast = toasts.find((toastItem) => toastItem.blockId === blockId) || toasts[0];

    if (!toast) return;

    resetPreviewToasts();

    [toast]
      .concat(toasts.filter((toastItem) => toastItem.blockId !== toast.blockId))
      .forEach((toastItem) => {
        showToast(toastItem, {
          delayBeforeShow: 0,
          displayDuration: 0,
          force: true
        });
      });
  }

  function previewAllToasts() {
    resetPreviewToasts();

    (config.toasts || [])
      .filter((toast) => toast.enabled)
      .forEach((toast) => {
        showToast(toast, {
          delayBeforeShow: 0,
          displayDuration: 0,
          force: true
        });
      });
  }

  if (isDesignMode) {
    previewAllToasts();

    addCleanup(document, 'shopify:section:select', (event) => {
      if (event.detail && event.detail.sectionId === config.sectionId) {
        previewAllToasts();
      }
    });

    addCleanup(document, 'shopify:block:select', (event) => {
      if (event.detail && event.detail.sectionId === config.sectionId) {
        previewToast(event.detail.blockId);
      }
    });
  } else {
    (config.toasts || []).forEach(queueToast);
  }

  addCleanup(stackToggle, 'click', (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (region.classList.contains('is-expanded')) {
      collapseStack();
    } else {
      expandStack();
    }
  });

  addCleanup(dismissAll, 'click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    dismissAllToasts();
  });

  addCleanup(
    region,
    'click',
    (event) => {
      if (!event.target.closest('.site-toast')) return;
      if (event.target.closest('.site-toast__close, .site-toast__action')) return;

      if (!region.classList.contains('is-expanded') && activeToasts.size > 1) {
        event.preventDefault();
        event.stopPropagation();
        expandStack();
      }
    },
    true
  );

  addCleanup(document, 'click', (event) => {
    if (!region.contains(event.target)) {
      collapseStack();
    }
  });

  const api = {
    show: showToast,
    dismiss: dismissToast,
    destroy() {
      cleanupHandlers.splice(0).forEach((cleanup) => cleanup());
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
      resetPreviewToasts();
      stackControls.remove();
      delete window.ConcepStoreToastInstances[config.sectionId];
    }
  };

  addCleanup(document, 'shopify:section:unload', (event) => {
    if (event.detail && event.detail.sectionId === config.sectionId) {
      api.destroy();
    }
  });

  window.ConcepStoreToastInstances[config.sectionId] = api;
  window.ConcepStoreToasts = api;
})();
